import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { batch, useDispatch, useSelector } from 'react-redux';
import { numbers, validators } from 'investira.sdk';
import services from '../services';
import {
  acDashboardPosicaoCacheInvalidated,
  acDashboardPosicaoCurrentKeyChanged,
  acDashboardPosicaoDateSelected,
  acDashboardPosicaoDatesChanged,
  acDashboardPosicaoViewsChanged,
  acDashboardPosicaoViewSelected,
  acDashboardPosicaoRequestFailed,
  acDashboardPosicaoRequestStarted,
  acDashboardPosicaoRequestSucceeded
} from '../store/Dashboard/DashboardActions';

// const DASHBOARD_POSICAO_STALE_TIME = 1 * 60 * 1000; // 1 minuto
// const DASHBOARD_POSICAO_STALE_TIME = 5 * 60 * 1000; // 5 minutos
const DASHBOARD_POSICAO_STALE_TIME = 30 * 60 * 1000; // 30 minutos
const DASHBOARD_CARTEIRAS_FAVORITAS_ID = '__DASHBOARD_CARTEIRAS_FAVORITAS__';
const DASHBOARD_CARTEIRAS_FAVORITAS_LABEL = 'Carteiras Favoritas';

const INITIAL_FILTERS = {
  views: [],
  selectedViewId: null,
  availableDatesByView: {},
  selectedDateByView: {}
};

const INITIAL_DATA = {
  highlights: {
    total_financeiro: 0,
    total_ativos: 0
  },
  carteirasChart: [],
  carteirasTable: [],
  charts: {
    ativos: [],
    emissores: [],
    gestores: [],
    setores: []
  },
  carteiras: [],
  posicoes: []
};

const INITIAL_DASHBOARD_CONTEXT = {
  portfolios: [],
  favoritas: {
    quantidade_carteiras: 0,
    datas: []
  },
  hasLoaded: false
};

/**
 * Converte valores numéricos do dashboard garantindo fallback para zero.
 * @param {*} pValue - Valor original a ser convertido
 * @returns {number} - Valor numérico seguro para operações matemáticas
 */
function getNumberOrZero(pValue) {
  return numbers.toNumber(pValue) ?? 0;
}

/**
 * Normaliza o erro para o formato consumido pela UI do dashboard.
 * @param {Object} pError - Erro original da request
 * @param {string} pFallbackMessage - Mensagem padrão da operação
 * @returns {{message: string}} - Erro normalizado
 */
function normalizeError(pError, pFallbackMessage) {
  return {
    message: pError?.message || pFallbackMessage
  };
}

/**
 * Identifica erros gerados pelo cancelamento explícito de requests.
 * @param {Object} pError - Erro retornado pela camada de request
 * @returns {boolean}
 */
function isRequestCanceled(pError) {
  const errorCode = pError?.code || pError?.error?.code;

  return Boolean(errorCode && errorCode.status === 404 && Number(errorCode.ref) === 1);
}

/**
 * Normaliza o valor de data para a chave usada no cache do dashboard.
 * @param {*} pValue - Valor original informado pela UI ou pelo service
 * @returns {string|null} - Data no formato YYYY-MM-DD
 */
function normalizeDateKey(pValue) {
  if (validators.isEmpty(pValue)) {
    return null;
  }

  const parsedDate = dayjs.isDayjs(pValue)
    ? pValue
    : dayjs(pValue?.data || pValue?.value || pValue);

  return parsedDate.isValid() ? parsedDate.format('YYYY-MM-DD') : null;
}

/**
 * Remove duplicidades e mantém as datas ordenadas da mais recente para a mais antiga.
 * @param {Array} pDates - Datas retornadas pelo service
 * @returns {string[]} - Datas normalizadas
 */
function normalizeDates(pDates = []) {
  if (!Array.isArray(pDates)) {
    return [];
  }

  const resolvedDates = Array.from(new Set(pDates.map(normalizeDateKey).filter(Boolean)));

  resolvedDates.sort((pDateA, pDateB) => dayjs(pDateB).valueOf() - dayjs(pDateA).valueOf());

  return resolvedDates;
}

/**
 * Cria a chave estável que identifica um snapshot de posição.
 * @param {*} pEntidadeId - Entidade atual
 * @param {*} pPortfolioId - Portfólio ou visão selecionada
 * @param {string|null} pDateKey - Data normalizada
 * @returns {string|null} - Chave do cache do dashboard
 */
function createCacheKey(pEntidadeId, pPortfolioId, pDateKey) {
  if (validators.isEmpty(pEntidadeId) || validators.isEmpty(pPortfolioId) || !pDateKey) {
    return null;
  }

  return `${pEntidadeId}:${pPortfolioId}:${pDateKey}`;
}

/**
 * Verifica se o snapshot em cache ainda está dentro da janela de freshness.
 * @param {Object} pCacheEntry - Entrada do cache do dashboard
 * @returns {boolean}
 */
function isCacheFresh(pCacheEntry) {
  if (!pCacheEntry?.fetchedAt) {
    return false;
  }

  return Date.now() - Number(pCacheEntry.fetchedAt) < DASHBOARD_POSICAO_STALE_TIME;
}

/**
 * Consolida os indicadores exibidos no topo da seção de posição.
 * @param {Array} pCarteiras - Carteiras já consolidadas
 * @returns {{total_financeiro: number, total_ativos: number}} - Totais da posição
 */
function getHighlights(pCarteiras = []) {
  return pCarteiras.reduce(
    (pAcc, pCarteira) => {
      pAcc.total_financeiro += getNumberOrZero(pCarteira.financeiro);
      pAcc.total_ativos += getNumberOrZero(pCarteira.quantidade_ativos);

      return pAcc;
    },
    {
      total_financeiro: 0,
      total_ativos: 0
    }
  );
}

/**
 * Consolida as carteiras usadas no gráfico principal.
 * Quando uma carteira não possui posições detalhadas no recorte,
 * utiliza os totais da própria carteira para manter a UI estável.
 * @param {Array} pCarteiras - Carteiras resolvidas com suas posições
 * @returns {Array} - Carteiras consolidadas e ordenadas
 */
function getCarteirasChart(pCarteiras = []) {
  const resolvedCarteiras = Object.values(
    pCarteiras.reduce((pAcc, pItem) => {
      const xCarteira = pItem.carteira || {};
      const xPosicoes = Array.isArray(pItem.posicoes) ? pItem.posicoes : [];
      const xKey = xCarteira.carteira_id;

      if (validators.isEmpty(xKey)) {
        return pAcc;
      }

      if (!pAcc[xKey]) {
        pAcc[xKey] = {
          carteira_id: xCarteira.carteira_id,
          carteira: xCarteira.carteira,
          data: xCarteira.data || xPosicoes[0]?.data || null,
          aberta: xCarteira.aberta ?? xPosicoes[0]?.aberta ?? false,
          quantidade: 0,
          financeiro: 0,
          quantidade_ativos: 0
        };
      }

      for (const xPosicao of xPosicoes) {
        pAcc[xKey].quantidade += getNumberOrZero(xPosicao.quantidade);
        pAcc[xKey].financeiro += getNumberOrZero(xPosicao.financeiro);
        pAcc[xKey].quantidade_ativos++;
      }

      return pAcc;
    }, {})
  );

  // Ordena primeiro pelo financeiro e depois pela data mais recente da posição.
  resolvedCarteiras.sort((pCarteiraA, pCarteiraB) => {
    const xFinanceiroDiff =
      getNumberOrZero(pCarteiraB.financeiro) - getNumberOrZero(pCarteiraA.financeiro);

    if (xFinanceiroDiff !== 0) {
      return xFinanceiroDiff;
    }

    return dayjs(pCarteiraB.data).valueOf() - dayjs(pCarteiraA.data).valueOf();
  });

  return resolvedCarteiras;
}

/**
 * Enriquece as carteiras com metadados usados apenas na tabela lateral.
 * @param {Array} pCarteirasChart - Carteiras consolidadas do gráfico
 * @returns {Array} - Carteiras com percentual e ângulos do donut
 */
function getCarteirasTable(pCarteirasChart = []) {
  const xTotalFinanceiro = pCarteirasChart.reduce((pAcc, pCarteira) => {
    return pAcc + getNumberOrZero(pCarteira.financeiro);
  }, 0);

  let xFinanceiroAcumulado = 0;

  return pCarteirasChart.map(pCarteira => {
    const xFinanceiro = getNumberOrZero(pCarteira.financeiro);
    const xPercentual = xTotalFinanceiro ? (xFinanceiro / xTotalFinanceiro) * 100 : null;
    const xStartAngle = xTotalFinanceiro
      ? (xFinanceiroAcumulado / xTotalFinanceiro) * 2 * Math.PI
      : 0;
    const xEndAngle = xTotalFinanceiro
      ? xStartAngle + (xFinanceiro / xTotalFinanceiro) * 2 * Math.PI
      : 0;

    xFinanceiroAcumulado += xFinanceiro;

    return {
      ...pCarteira,
      percentual: xPercentual,
      startAngle: xStartAngle,
      endAngle: xEndAngle,
      financeiroAcumulado: xFinanceiroAcumulado
    };
  });
}

/**
 * Normaliza as posições de ativos preservando os campos necessários
 * para o agrupamento mercado -> tipo de ativo no componente.
 * @param {Array} pPosicoes - Posições consolidadas do dashboard
 * @returns {Array} - Lista de ativos normalizada para o gráfico
 */
function getAtivosChart(pPosicoes = []) {
  return pPosicoes.reduce((pAcc, pPosicao) => {
    const { mercado, mercado_descricao, ativo_tipo, ativo_tipo_id } = pPosicao;

    if (validators.isEmpty(mercado)) {
      return pAcc;
    }

    pAcc.push({
      ...pPosicao,
      mercado,
      mercado_descricao,
      ativo_tipo,
      ativo_tipo_id,
      quantidade: getNumberOrZero(pPosicao.quantidade),
      financeiro: getNumberOrZero(pPosicao.financeiro),
      quantidade_ativos: 1
    });

    return pAcc;
  }, []);
}

/**
 * Agrupa as posições por emissor para alimentar o gráfico de emissores.
 * @param {Array} pPosicoes - Posições consolidadas do dashboard
 * @returns {Array} - Dados agrupados por emissor
 */
function getEmissoresChart(pPosicoes = []) {
  return Object.values(
    pPosicoes.reduce((pAcc, pPosicao) => {
      const { emissor } = pPosicao;
      const xKey = emissor?.cnpj || 'SEM_EMISSOR';

      if (!validators.isEmpty(xKey)) {
        if (!pAcc[xKey]) {
          pAcc[xKey] = {
            cnpj: emissor?.cnpj || null,
            pessoa_id: emissor?.pessoa_id || null,
            logo_pessoa_id: emissor?.logo_pessoa_id || null,
            pessoa: emissor?.pessoa || 'Sem emissor',
            pessoa_fantasia: emissor?.pessoa_fantasia || 'Sem emissor',
            quantidade: 0,
            financeiro: 0,
            quantidade_ativos: 0
          };
        }

        pAcc[xKey].quantidade += getNumberOrZero(pPosicao.quantidade);
        pAcc[xKey].financeiro += getNumberOrZero(pPosicao.financeiro);
        pAcc[xKey].quantidade_ativos++;
      }

      return pAcc;
    }, {})
  );
}

/**
 * Agrupa as posições por gestor para alimentar o gráfico de gestores.
 * @param {Array} pPosicoes - Posições consolidadas do dashboard
 * @returns {Array} - Dados agrupados por gestor
 */
function getGestoresChart(pPosicoes = []) {
  return Object.values(
    pPosicoes.reduce((pAcc, pPosicao) => {
      const { gestor } = pPosicao;
      const xKey = gestor?.cnpj;

      if (!validators.isEmpty(xKey)) {
        if (!pAcc[xKey]) {
          pAcc[xKey] = {
            ...gestor,
            pessoa: gestor?.pessoa || 'Sem gestor',
            pessoa_fantasia: gestor?.pessoa_fantasia || 'Sem gestor',
            quantidade: 0,
            financeiro: 0,
            quantidade_ativos: 0
          };
        }

        pAcc[xKey].quantidade += getNumberOrZero(pPosicao.quantidade);
        pAcc[xKey].financeiro += getNumberOrZero(pPosicao.financeiro);
        pAcc[xKey].quantidade_ativos++;
      }

      return pAcc;
    }, {})
  );
}

/**
 * Agrupa as posições por setor para alimentar o gráfico de setores.
 * @param {Array} pPosicoes - Posições consolidadas do dashboard
 * @returns {Array} - Dados agrupados por setor
 */
function getSetoresChart(pPosicoes = []) {
  return Object.values(
    pPosicoes.reduce((pAcc, pPosicao) => {
      const { emissor, emissor_setor_id, emissor_top_setor_id } = pPosicao;
      const xKey = emissor_setor_id || 'SEM_SETOR';

      if (!validators.isEmpty(xKey)) {
        if (!pAcc[xKey]) {
          pAcc[xKey] = {
            setor: emissor?.setor || 'Sem setor',
            top_setor: emissor?.top_setor || 'Sem setor',
            emissor_setor_id: emissor_setor_id || null,
            emissor_top_setor_id: emissor_top_setor_id || null,
            quantidade: 0,
            financeiro: 0,
            quantidade_ativos: 0
          };
        }

        pAcc[xKey].quantidade += getNumberOrZero(pPosicao.quantidade);
        pAcc[xKey].financeiro += getNumberOrZero(pPosicao.financeiro);
        pAcc[xKey].quantidade_ativos++;
      }

      return pAcc;
    }, {})
  );
}

/**
 * Monta o snapshot consolidado consumido pela seção de posição do dashboard.
 * Centraliza em um único ponto toda a transformação necessária para a UI.
 * @param {Array} pCarteiras - Carteiras resolvidas com suas posições
 * @returns {Object} - Snapshot final da posição
 */
function buildDashboard(pCarteiras = []) {
  const carteirasChart = getCarteirasChart(pCarteiras);
  const carteirasTable = getCarteirasTable(carteirasChart);
  const posicoes = pCarteiras.flatMap(({ posicoes = [] }) => posicoes);

  return {
    highlights: getHighlights(carteirasChart),
    carteirasChart,
    carteirasTable,
    charts: {
      ativos: getAtivosChart(posicoes),
      emissores: getEmissoresChart(posicoes),
      gestores: getGestoresChart(posicoes),
      setores: getSetoresChart(posicoes)
    },
    carteiras: pCarteiras.map(({ carteira = {} }) => carteira),
    posicoes
  };
}

/**
 * Adapta o contrato callback-first dos services para uso com async/await.
 * @param {Function} pService - Service a ser executado
 * @param {Object} pParams - Parâmetros da chamada
 * @returns {Promise<any>} - Resultado assíncrono da chamada
 */
function requestAsync(pService, pParams) {
  // Os services do projeto seguem contrato callback-first.
  return new Promise((resolve, reject) => {
    pService(pParams, resolve, reject);
  });
}

/**
 * Normaliza os portfólios retornados pelo backend preservando datas e metadados.
 * @param {Array} pPortfolios - Portfólios retornados pelo service do dashboard
 * @returns {Array} - Portfólios normalizados para consumo do hook
 */
function normalizeDashboardPortfolios(pPortfolios = []) {
  if (!Array.isArray(pPortfolios)) {
    return [];
  }

  return pPortfolios
    .map(pPortfolio => {
      return {
        ...pPortfolio,
        datas: normalizeDates(pPortfolio?.datas || [])
      };
    })
    .filter(pPortfolio => pPortfolio.datas.length > 0);
}

/**
 * Normaliza o contexto da visão de carteiras favoritas retornado pelo backend.
 * @param {Object} pFavoritas - Contexto retornado pelo backend
 * @returns {{quantidade_carteiras: number, datas: Array}} - Favoritas normalizadas
 */
function normalizeDashboardFavoritas(pFavoritas = {}) {
  const resolvedDates = normalizeDates(pFavoritas?.datas || []);

  return {
    quantidade_carteiras: resolvedDates.length
      ? getNumberOrZero(pFavoritas?.quantidade_carteiras)
      : 0,
    datas: resolvedDates
  };
}

/**
 * Monta a lista final de visões exibidas na UI do dashboard.
 * @param {Array} pPortfolios - Portfólios normalizados do backend
 * @param {Object} pFavoritas - Contexto normalizado das favoritas
 * @returns {Array} - Visões disponíveis para o combo
 */
function buildDashboardViews(pPortfolios = [], pFavoritas = {}) {
  const resolvedViews = Array.isArray(pPortfolios) ? [...pPortfolios] : [];
  const resolvedFavoritasDates = normalizeDates(pFavoritas?.datas || []);

  if (getNumberOrZero(pFavoritas?.quantidade_carteiras) > 0 && resolvedFavoritasDates.length > 0) {
    resolvedViews.unshift({
      portfolio_id: DASHBOARD_CARTEIRAS_FAVORITAS_ID,
      portfolio: DASHBOARD_CARTEIRAS_FAVORITAS_LABEL,
      quantidade_carteiras: getNumberOrZero(pFavoritas.quantidade_carteiras),
      datas: resolvedFavoritasDates,
      isCarteirasFavoritas: true
    });
  }

  return resolvedViews;
}

/**
 * Monta o mapa de datas disponíveis por visão com base na resposta do backend.
 * @param {Array} pViews - Visões disponíveis no dashboard
 * @returns {Object} - Mapa view_id -> datas disponíveis
 */
function buildAvailableDatesByView(pViews = []) {
  if (!Array.isArray(pViews)) {
    return {};
  }

  return pViews.reduce((pAcc, pView) => {
    if (!validators.isEmpty(pView?.portfolio_id)) {
      pAcc[pView.portfolio_id] = normalizeDates(pView?.datas || []);
    }

    return pAcc;
  }, {});
}

/**
 * Resolve o identificador de visão a partir do bloco selected retornado pelo backend.
 * @param {Object} pSelected - Seleção efetiva do backend
 * @returns {*|null} - Identificador da visão correspondente no front
 */
function resolveSelectedViewIdFromDashboard(pSelected = {}) {
  if (pSelected?.visao_tipo === 'favoritas') {
    return DASHBOARD_CARTEIRAS_FAVORITAS_ID;
  }

  return pSelected?.portfolio_id || null;
}

/**
 * Monta os parâmetros enviados ao service do dashboard priorizando a seleção atual.
 * Quando ainda não existe seleção local, usa favoritas como default do primeiro carregamento
 * se houver ao menos uma carteira favorita disponível no preload.
 * @param {Object} pOptions - Opções usadas para construir a request
 * @returns {Object} - Parâmetros consumidos pela rota do dashboard
 */
function buildDashboardRequestParams(pOptions = {}) {
  const {
    selectedViewId = null,
    selectedDateKey = null,
    preferredPortfolioId = null,
    hasCarteirasFavoritas = false
  } = pOptions;

  const resolvedParams = {};

  if (selectedViewId === DASHBOARD_CARTEIRAS_FAVORITAS_ID) {
    resolvedParams.visao_tipo = 'favoritas';
  } else if (!validators.isEmpty(selectedViewId)) {
    resolvedParams.visao_tipo = 'portfolio';
    resolvedParams.portfolio_id = selectedViewId;
  } else if (hasCarteirasFavoritas) {
    resolvedParams.visao_tipo = 'favoritas';
  } else if (!validators.isEmpty(preferredPortfolioId)) {
    resolvedParams.visao_tipo = 'portfolio';
    resolvedParams.portfolio_id = preferredPortfolioId;
  }

  if (selectedDateKey) {
    resolvedParams.data_referencia = selectedDateKey;
  }

  return resolvedParams;
}

/**
 * Consolida o contexto necessário para uma request do dashboard.
 * Centraliza params, assinatura da request e chave de cache esperada.
 * @param {Object} pOptions - Dados correntes de seleção e contexto da entidade
 * @returns {{params: Object, signature: string, cacheKey: (string|null)}} - Contexto da request
 */
function buildDashboardRequestContext(pOptions = {}) {
  const { entidadeId = null, selectedViewId = null, selectedDateKey = null } = pOptions;

  const params = buildDashboardRequestParams(pOptions);
  const signature = JSON.stringify(params);
  const cacheKey = createCacheKey(entidadeId, selectedViewId, selectedDateKey);

  return {
    params,
    signature,
    cacheKey
  };
}

/**
 * Resolve as condições de carregamento do dashboard em regras nomeadas.
 * Separa bootstrap de refresh para deixar explícito o que ainda falta
 * na seleção atual antes de consultar o cache.
 * @param {Object} pOptions - Estado corrente usado para decidir a carga
 * @returns {{shouldBootstrapDashboard: boolean, shouldRefetchDashboard: boolean, shouldLoadDashboard: boolean}} - Flags derivadas de carregamento
 */
function getDashboardLoadState(pOptions = {}) {
  const {
    canLoadDashboard = true,
    hasLoadedDashboardContext = false,
    hasAvailableViews = false,
    selectedViewId = null,
    hasSelectedViewAvailableDates = false,
    selectedDateKey = null,
    currentKey = null,
    currentCacheEntry = null
  } = pOptions;

  if (!canLoadDashboard) {
    return {
      shouldBootstrapDashboard: false,
      shouldRefetchDashboard: false,
      shouldLoadDashboard: false
    };
  }

  const hasResolvedEmptyDashboard = hasLoadedDashboardContext && !hasAvailableViews;

  if (hasResolvedEmptyDashboard) {
    return {
      shouldBootstrapDashboard: false,
      shouldRefetchDashboard: false,
      shouldLoadDashboard: false
    };
  }

  const needsDashboardContext = !hasLoadedDashboardContext;
  const needsSelectedView = hasAvailableViews && !selectedViewId;
  const needsSelectedDate = Boolean(
    selectedViewId && hasSelectedViewAvailableDates && !selectedDateKey
  );
  const needsCurrentSnapshot = Boolean(selectedViewId && selectedDateKey && !currentKey);

  const shouldBootstrapDashboard =
    needsDashboardContext || needsSelectedView || needsSelectedDate || needsCurrentSnapshot;
  const shouldRefetchDashboard = !shouldBootstrapDashboard && !isCacheFresh(currentCacheEntry);

  return {
    shouldBootstrapDashboard,
    shouldRefetchDashboard,
    shouldLoadDashboard: shouldBootstrapDashboard || shouldRefetchDashboard
  };
}

/**
 * Hook fachada da página Dashboard.
 * Centraliza filtros, cache e carregamento da seção de posição.
 * @returns {{posicao: Object}} - API pública da página para a seção de posição
 */
function useDashboard() {
  const dispatch = useDispatch();

  const entidadeId = useSelector(store => store.entidade.current.default_entidade_id);
  const storeCarteiras = useSelector(store => store.carteiras.data);
  const storePortfolios = useSelector(store => store.portfolios.data);
  const storePortfolioCurrent = useSelector(store => store.portfolios.current);
  const storePortfoliosUpdatedAt = useSelector(store => store.portfolios.updatedAt);
  const dashboardState = useSelector(store => store.dashboard?.posicao);

  const [dashboardContext, setDashboardContext] = useState(INITIAL_DASHBOARD_CONTEXT);
  const [requestState, setRequestState] = useState({
    isLoading: false,
    error: null
  });

  const activeRequestsRef = useRef({
    isMounted: true,
    dashboard: 0,
    dashboardSignature: null
  });
  const previousEntidadeIdRef = useRef(entidadeId);

  const {
    filters = INITIAL_FILTERS,
    currentKey = null,
    cacheByKey = {},
    requestsByKey = {}
  } = dashboardState || {};

  const fallbackViews = useMemo(() => {
    return buildDashboardViews(
      normalizeDashboardPortfolios(Array.isArray(storePortfolios) ? storePortfolios : []),
      {
        quantidade_carteiras: Array.isArray(storeCarteiras) ? storeCarteiras.length : 0,
        datas: []
      }
    );
  }, [storeCarteiras, storePortfolios]);

  const views = useMemo(() => {
    if (dashboardContext.hasLoaded) {
      return buildDashboardViews(dashboardContext.portfolios, dashboardContext.favoritas);
    }

    return fallbackViews;
  }, [dashboardContext, fallbackViews]);

  const selectedViewId = filters.selectedViewId;
  const availableDatesByView = filters.availableDatesByView;
  const selectedDateByView = filters.selectedDateByView;

  const selectedView = useMemo(() => {
    return views.find(view => view.portfolio_id === selectedViewId) || null;
  }, [selectedViewId, views]);

  const availableDates = useMemo(() => {
    return selectedViewId ? availableDatesByView[selectedViewId] || [] : [];
  }, [availableDatesByView, selectedViewId]);

  const selectedDateKey = selectedViewId ? selectedDateByView[selectedViewId] || null : null;

  const currentCacheEntry = currentKey ? cacheByKey[currentKey] : null;
  const currentRequest = currentKey ? requestsByKey[currentKey] : null;
  const hasCurrentData = Boolean(currentCacheEntry?.data);
  const hasLoadedPortfolios = !validators.isNull(storePortfoliosUpdatedAt);
  const hasCarteirasFavoritas = Array.isArray(storeCarteiras) && storeCarteiras.length > 0;
  const canBootstrapDashboard =
    !hasLoadedPortfolios || hasCarteirasFavoritas || dashboardContext.hasLoaded;
  const hasAvailableViews = views.length > 0;
  const selectedViewHasAvailableDates = availableDates.length > 0;

  /**
   * Deriva o contexto da request a partir da seleção atual e do fallback inicial.
   * É reutilizado tanto na carga quanto na deduplicação de requests equivalentes.
   */
  const dashboardRequestContext = useMemo(() => {
    return buildDashboardRequestContext({
      entidadeId,
      selectedViewId,
      selectedDateKey,
      preferredPortfolioId: storePortfolioCurrent?.portfolio_id,
      hasCarteirasFavoritas
    });
  }, [
    entidadeId,
    hasCarteirasFavoritas,
    selectedDateKey,
    selectedViewId,
    storePortfolioCurrent?.portfolio_id
  ]);

  const { shouldLoadDashboard } = getDashboardLoadState({
    canLoadDashboard: canBootstrapDashboard,
    hasLoadedDashboardContext: dashboardContext.hasLoaded,
    hasAvailableViews,
    selectedViewId,
    hasSelectedViewAvailableDates: selectedViewHasAvailableDates,
    selectedDateKey,
    currentKey,
    currentCacheEntry
  });
  const isCurrentDashboardRequestPending =
    requestState.isLoading &&
    activeRequestsRef.current.dashboardSignature === dashboardRequestContext.signature;

  /**
   * Garante que apenas a request mais recente de cada grupo possa atualizar o estado.
   * @param {string} pRequestKey - Grupo lógico da request
   * @param {number|string} pRequestId - Identificador gerado para a request
   * @returns {boolean} - Indica se a request ainda é a ativa
   */
  const hasActiveRequest = useCallback((pRequestKey, pRequestId) => {
    return (
      activeRequestsRef.current.isMounted && activeRequestsRef.current[pRequestKey] === pRequestId
    );
  }, []);

  /**
   * Cancela todas as requests em andamento disparadas pelos services do dashboard.
   */
  const cancelDashboardServices = useCallback(() => {
    services.dashboard.cancel?.posicao?.cancel?.();
  }, []);

  /**
   * Cancela as requests em andamento e reseta os estados locais de loading.
   * É usado principalmente na troca de entidade para evitar travas visuais.
   */
  const cancelDashboardRequests = useCallback(() => {
    cancelDashboardServices();

    activeRequestsRef.current.dashboard += 1;
    activeRequestsRef.current.dashboardSignature = null;
    setDashboardContext(INITIAL_DASHBOARD_CONTEXT);

    setRequestState({
      isLoading: false,
      error: null
    });
  }, [cancelDashboardServices]);

  /**
   * Gera um novo identificador para o grupo de request do dashboard.
   * @returns {number} - Identificador incremental da request
   */
  const createRequestId = useCallback(() => {
    const requestId = activeRequestsRef.current.dashboard + 1;

    activeRequestsRef.current.dashboard = requestId;

    return requestId;
  }, []);

  /**
   * Sincroniza o slice local com o contexto e a seleção efetiva devolvidos pelo backend.
   * @param {Object} pResponse - Resposta normalizada da rota do dashboard
   * @returns {{cacheKey: (string|null), data: Object}} - Snapshot resolvido e sua chave de cache
   */
  const syncDashboardResponse = useCallback(
    pResponse => {
      const resolvedPortfolios = normalizeDashboardPortfolios(pResponse?.portfolios || []);
      const resolvedFavoritas = normalizeDashboardFavoritas(pResponse?.favoritas || {});
      const resolvedViews = buildDashboardViews(resolvedPortfolios, resolvedFavoritas);
      const resolvedAvailableDates = buildAvailableDatesByView(resolvedViews);
      const resolvedSelectedViewId = resolveSelectedViewIdFromDashboard(pResponse?.selected);
      const resolvedSelectedDateKey = normalizeDateKey(pResponse?.selected?.data);
      const resolvedCarteirasPosicoes = Array.isArray(pResponse?.carteiras_posicoes)
        ? pResponse.carteiras_posicoes
        : [];

      setDashboardContext({
        portfolios: resolvedPortfolios,
        favoritas: resolvedFavoritas,
        hasLoaded: true
      });

      // Agrupa a sincronização dos filtros para evitar renders intermediários no hook.
      batch(() => {
        dispatch(acDashboardPosicaoViewsChanged(resolvedViews));

        Object.entries(resolvedAvailableDates).forEach(([xViewId, xDates]) => {
          dispatch(
            acDashboardPosicaoDatesChanged({
              viewId: xViewId,
              dates: xDates
            })
          );
        });

        if (resolvedSelectedViewId !== selectedViewId) {
          dispatch(acDashboardPosicaoViewSelected(resolvedSelectedViewId));
        }

        if (
          resolvedSelectedViewId &&
          (selectedDateByView[resolvedSelectedViewId] || null) !== resolvedSelectedDateKey
        ) {
          dispatch(
            acDashboardPosicaoDateSelected({
              viewId: resolvedSelectedViewId,
              date: resolvedSelectedDateKey
            })
          );
        }
      });

      return {
        cacheKey: createCacheKey(entidadeId, resolvedSelectedViewId, resolvedSelectedDateKey),
        data: buildDashboard(resolvedCarteirasPosicoes)
      };
    },
    [dispatch, entidadeId, selectedDateByView, selectedViewId]
  );

  /**
   * Busca o contexto e o snapshot atual do dashboard em uma única chamada ao backend.
   * O hook segue responsável apenas por sincronizar os filtros locais e montar a UI.
   */
  const loadDashboardData = useCallback(() => {
    if (!entidadeId || !hasLoadedPortfolios) {
      return;
    }

    const requestId = createRequestId();

    activeRequestsRef.current.dashboardSignature = dashboardRequestContext.signature;

    setRequestState({
      isLoading: true,
      error: null
    });

    if (dashboardRequestContext.cacheKey) {
      dispatch(
        acDashboardPosicaoRequestStarted({
          cacheKey: dashboardRequestContext.cacheKey,
          requestId
        })
      );
    }

    requestAsync(services.dashboard.posicao, dashboardRequestContext.params)
      .then(pResponse => {
        if (!hasActiveRequest('dashboard', requestId)) {
          return;
        }

        const resolvedSnapshot = syncDashboardResponse(pResponse?.data || {});
        const resolvedRequestCacheKey =
          resolvedSnapshot.cacheKey || dashboardRequestContext.cacheKey;

        if (
          resolvedRequestCacheKey &&
          resolvedRequestCacheKey !== dashboardRequestContext.cacheKey
        ) {
          batch(() => {
            dispatch(
              acDashboardPosicaoRequestStarted({
                cacheKey: resolvedRequestCacheKey,
                requestId
              })
            );
          });
        }

        if (resolvedSnapshot.cacheKey) {
          // Mantém chave atual e snapshot em um único ciclo de atualização do store.
          batch(() => {
            dispatch(acDashboardPosicaoCurrentKeyChanged(resolvedSnapshot.cacheKey));
            dispatch(
              acDashboardPosicaoRequestSucceeded({
                cacheKey: resolvedSnapshot.cacheKey,
                requestId,
                data: resolvedSnapshot.data,
                fetchedAt: Date.now()
              })
            );
          });
        }

        setRequestState({
          isLoading: false,
          error: null
        });

        if (hasActiveRequest('dashboard', requestId)) {
          activeRequestsRef.current.dashboardSignature = null;
        }
      })
      .catch(pError => {
        if (isRequestCanceled(pError)) {
          return;
        }

        if (!hasActiveRequest('dashboard', requestId)) {
          return;
        }

        const resolvedError = normalizeError(
          pError,
          'Não foi possível carregar a posição do dashboard.'
        );

        if (dashboardRequestContext.cacheKey) {
          dispatch(
            acDashboardPosicaoRequestFailed({
              cacheKey: dashboardRequestContext.cacheKey,
              requestId,
              error: resolvedError
            })
          );
        }

        setRequestState({
          isLoading: false,
          error: resolvedError
        });

        if (hasActiveRequest('dashboard', requestId)) {
          activeRequestsRef.current.dashboardSignature = null;
        }
      });
  }, [
    createRequestId,
    dashboardRequestContext,
    dispatch,
    entidadeId,
    hasActiveRequest,
    hasLoadedPortfolios,
    syncDashboardResponse
  ]);

  useEffect(() => {
    const currentActiveRequests = activeRequestsRef.current;

    return () => {
      currentActiveRequests.isMounted = false;
      cancelDashboardServices();
    };
  }, [cancelDashboardServices]);

  useEffect(() => {
    if (previousEntidadeIdRef.current !== entidadeId) {
      cancelDashboardRequests();
      previousEntidadeIdRef.current = entidadeId;
    }
  }, [cancelDashboardRequests, entidadeId]);

  useEffect(() => {
    if (!entidadeId || !hasLoadedPortfolios) {
      return;
    }

    if (isCurrentDashboardRequestPending) {
      return;
    }

    if (!shouldLoadDashboard) {
      return;
    }

    loadDashboardData();
  }, [
    entidadeId,
    hasLoadedPortfolios,
    isCurrentDashboardRequestPending,
    loadDashboardData,
    shouldLoadDashboard
  ]);

  useEffect(() => {
    const nextKey = createCacheKey(entidadeId, selectedViewId, selectedDateKey);

    if (nextKey !== currentKey) {
      dispatch(acDashboardPosicaoCurrentKeyChanged(nextKey));
    }
  }, [currentKey, dispatch, entidadeId, selectedDateKey, selectedViewId]);

  useEffect(() => {
    if (!hasAvailableViews) {
      return;
    }

    if (!currentKey || !selectedViewId || !selectedDateKey) {
      return;
    }

    if (!currentCacheEntry?.fetchedAt || !isCacheFresh(currentCacheEntry)) {
      return;
    }

    if (requestState.isLoading) {
      return;
    }

    const cacheAge = Date.now() - Number(currentCacheEntry.fetchedAt);
    const timeoutMs = Math.max(DASHBOARD_POSICAO_STALE_TIME - cacheAge, 0);
    const timeoutId = setTimeout(() => {
      loadDashboardData();
    }, timeoutMs);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [
    hasAvailableViews,
    currentCacheEntry,
    currentKey,
    loadDashboardData,
    requestState.isLoading,
    selectedDateKey,
    selectedViewId
  ]);

  const selectView = useCallback(
    pView => {
      const viewId = typeof pView === 'string' ? pView : pView?.portfolio_id || null;

      if (!viewId || viewId === selectedViewId) {
        return;
      }

      dispatch(acDashboardPosicaoViewSelected(viewId));
    },
    [dispatch, selectedViewId]
  );

  const selectDate = useCallback(
    pDate => {
      if (!selectedViewId) {
        return;
      }

      const nextDateKey = normalizeDateKey(pDate);

      if (nextDateKey === selectedDateKey) {
        return;
      }

      dispatch(
        acDashboardPosicaoDateSelected({
          viewId: selectedViewId,
          date: nextDateKey
        })
      );
    },
    [dispatch, selectedDateKey, selectedViewId]
  );

  const invalidate = useCallback(
    (pCacheKey = currentKey) => {
      dispatch(acDashboardPosicaoCacheInvalidated(pCacheKey || null));
    },
    [currentKey, dispatch]
  );

  const refetch = useCallback(() => {
    if (!currentKey) {
      return;
    }

    dispatch(acDashboardPosicaoCacheInvalidated(currentKey));
  }, [currentKey, dispatch]);

  const currentData = currentCacheEntry?.data || INITIAL_DATA;
  const currentError = requestState.error || currentRequest?.error || null;
  const lastUpdated = currentCacheEntry?.fetchedAt || currentRequest?.updatedAt || Date.now();
  const isLoadingPortfolios = Boolean(entidadeId) && !hasLoadedPortfolios;
  const isLoadingViews =
    Boolean(entidadeId) && (isLoadingPortfolios || (!hasAvailableViews && requestState.isLoading));
  const isLoadingDashboard = requestState.isLoading;
  const isLoadingDates = isLoadingDashboard;
  const isLoadingPosicoes = isLoadingDashboard;
  const isInitialLoading = !hasCurrentData && (isLoadingPortfolios || isLoadingDashboard);
  const isRefreshing = hasCurrentData && isLoadingDashboard;

  return useMemo(() => {
    return {
      posicao: {
        filters: {
          views,
          selectedView,
          availableDates,
          selectedDate: selectedDateKey ? dayjs(selectedDateKey) : null
        },
        state: {
          isInitialLoading,
          isLoading: isInitialLoading,
          isLoadingPortfolios,
          isLoadingViews,
          isLoadingDates,
          isLoadingPosicoes,
          isRefreshing,
          lastUpdated,
          error: currentError,
          staleTime: DASHBOARD_POSICAO_STALE_TIME
        },
        data: currentData,
        actions: {
          selectView,
          selectPortfolio: selectView,
          selectDate,
          refetch,
          invalidate
        }
      }
    };
  }, [
    availableDates,
    currentData,
    currentError,
    invalidate,
    isInitialLoading,
    isLoadingDates,
    isLoadingPosicoes,
    isLoadingPortfolios,
    isLoadingViews,
    isRefreshing,
    lastUpdated,
    views,
    refetch,
    selectDate,
    selectView,
    selectedDateKey,
    selectedView
  ]);
}

export default useDashboard;
